import * as React from "react";
import { useDirection } from "@radix-ui/react-direction";
import { format, type Locale } from "date-fns";
import { arSA } from "date-fns/locale/ar-SA";
import { enUS } from "date-fns/locale/en-US";
import type { Labels } from "react-day-picker";

export type PickerLang = "ar" | "en";
export type PickerDir = "rtl" | "ltr";

/**
 * Fallback copy for the date / time pickers. Apps normally pass translated strings through
 * props (from `packages/i18n`); these defaults only apply when a prop is omitted so the
 * shared components never fall back to English inside an Arabic UI.
 */
export interface PickerStrings {
  pickDate: string;
  pickDateRange: string;
  selectTime: string;
  hour: string;
  minute: string;
  period: string;
  am: string;
  pm: string;
  setTime: string;
}

const STRINGS: Record<PickerLang, PickerStrings> = {
  en: {
    pickDate: "Pick a date",
    pickDateRange: "Pick a date range",
    selectTime: "Select time",
    hour: "Hour",
    minute: "Minute",
    period: "Period",
    am: "AM",
    pm: "PM",
    setTime: "Set time",
  },
  ar: {
    pickDate: "اختر تاريخًا",
    pickDateRange: "اختر نطاق التاريخ",
    selectTime: "اختر الوقت",
    hour: "الساعة",
    minute: "الدقيقة",
    period: "الفترة",
    am: "ص",
    pm: "م",
    setTime: "تعيين الوقت",
  },
};

const DATE_FNS_LOCALES: Record<PickerLang, Locale> = { en: enUS, ar: arSA };

/** react-day-picker aria-labels (its defaults are English-only). */
const CALENDAR_LABELS: Record<PickerLang, Partial<Labels>> = {
  en: {},
  ar: {
    labelNav: () => "التنقل بين الأشهر",
    labelPrevious: () => "الشهر السابق",
    labelNext: () => "الشهر التالي",
    labelMonthDropdown: () => "الشهر: ",
    labelYearDropdown: () => "السنة: ",
    labelDayButton: (date, modifiers) => {
      let label = format(date, "PPPP", { locale: arSA });
      if (modifiers.today) label = `اليوم، ${label}`;
      if (modifiers.selected) label = `${label}، محدد`;
      return label;
    },
  },
};

export interface PickerLocale {
  lang: PickerLang;
  dir: PickerDir;
  /** date-fns locale for `format()` and react-day-picker / react-datepicker. */
  locale: Locale;
  strings: PickerStrings;
  calendarLabels: Partial<Labels>;
}

// `<html dir lang>` is what the portals (initI18n / switchLanguage) and the website layout set.
// Subscribing to it keeps open pickers in sync with a runtime language switch, and the server
// snapshot keeps SSR (website) and the first client render identical, so hydration stays clean.
function subscribeToDocumentLocale(onChange: () => void) {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["dir", "lang"],
  });
  return () => observer.disconnect();
}
const getDocumentLocale = () => `${document.documentElement.dir}|${document.documentElement.lang}`;
const getServerDocumentLocale = () => "|";

/**
 * Resolve language + text direction for the shared pickers.
 *
 * The portals (admin / customer / vendor) wrap the tree in Radix `DirectionProvider`; the
 * website only sets `<html dir lang>`. Reading both covers every host app. Arabic is the only
 * RTL language the product ships, so RTL implies Arabic copy and locale.
 */
export function usePickerLocale(): PickerLocale {
  const providerDir = useDirection();
  const [documentDir, documentLang] = React.useSyncExternalStore(
    subscribeToDocumentLocale,
    getDocumentLocale,
    getServerDocumentLocale,
  ).split("|");
  const dir: PickerDir = providerDir === "rtl" || documentDir === "rtl" ? "rtl" : "ltr";
  const lang: PickerLang =
    dir === "rtl" || documentLang.toLowerCase().startsWith("ar") ? "ar" : "en";

  return React.useMemo(
    () => ({
      lang,
      dir,
      locale: DATE_FNS_LOCALES[lang],
      strings: STRINGS[lang],
      calendarLabels: CALENDAR_LABELS[lang],
    }),
    [lang, dir],
  );
}
